import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CategoryAutocompleteInput } from "./CategoryAutocompleteInput";
import { CategorySuggestions } from "./CategorySuggestions";
import { useCategoryInitialization } from "./CategorySuggestions/hooks/useCategoryInitialization";
import { GbpCategories } from "./types";
import { LocationData } from "../../../../../models/LocationData";
import { OptionalRender } from "../../../../Common/OptionalRender";
import { HookFormError } from "../../../../HookForm/Error";

interface IHookFormGbpCategoriesProps {
  locationData: LocationData;
}

export const GbpCategoriesInput = ({
  locationData,
}: IHookFormGbpCategoriesProps) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.modalEditProfile.categoryForm.${key}`);

  const form = useFormContext<GbpCategories>();

  const { control, formState } = form;

  // FIXME: Either Zod or React Hook Form don't seem to want to attach errors to an array field,
  // and the error shifts between these two places.
  const error =
    formState.errors.additionalCategories?.message ??
    formState.errors.additionalCategories?.root?.message ??
    undefined;

  const additionalCategories = useFieldArray({
    control,
    name: "additionalCategories",
  });

  const onAddSubCategory = (name = "") => {
    additionalCategories.append({ name });
  };

  const { categoriesInitialized } = useCategoryInitialization(
    form,
    locationData
  );

  if (!categoriesInitialized) {
    return <p>{t("loading")}</p>;
  }

  return (
    <>
      <small>{t("translation_explanation")}</small>
      <h3>{t("main_category")}</h3>
      <CategoryAutocompleteInput name={`primaryCategory.name`} />
      <h3>{t("sub_categories")}</h3>
      {additionalCategories.fields.map((item, index) => {
        return (
          <div key={item.id} className="subcategory-row">
            <div className="subcategory-row-input-container">
              <CategoryAutocompleteInput
                name={`additionalCategories.${index}.name`}
              />
            </div>
            <button
              type="button"
              className="btn btn-border btn-border-red"
              onClick={() => additionalCategories.remove(index)}
            >
              {t("remove")}
            </button>
          </div>
        );
      })}
      <div>
        <button
          type="button"
          className="btn btn-border"
          onClick={() => onAddSubCategory()}
        >
          {t("add_sub_category")}
        </button>
        {
          <OptionalRender mustBeImpersonating={true}>
            <hr />
            <CategorySuggestions
              locationId={locationData.id}
              onSelectSuggestion={onAddSubCategory}
            />
            <hr />
          </OptionalRender>
        }
      </div>
      {<HookFormError error={error} />}
    </>
  );
};
